import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header, Sidebar, Breadcrumb, NotifyBox, Footer } from '../../components';
import Loading from '../../components/loading';
import { StoreProductsService } from '../../services/store-products';
import { useProductForm } from '../../hooks/useProductForm';
import './management-store-product-new.scss';

const PRODUCT_FIELD_LABELS = {
  name: 'Nome',
  currency: 'Moeda',
  amount: 'Valor',
  unique_name: 'Identificação única do produto',
  installments_max: 'Parcelas',
};

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'Real (BRL)' },
  { value: 'USD', label: 'Dólar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

export default function ViewManagementStoreProductNew({ consumer, entity, history }) {
  const { connectorUid } = useParams();
  const [messages, setMessages] = useState({});
  const [connectorInfo, setConnectorInfo] = useState(null);
  const [preferences, setPreferences] = useState(null);

  // Usar o hook personalizado
  const {
    product,
    saving,
    errors,
    isFormValid,
    updateField,
    updateInstallment,
    saveProduct,
    generateUniqueName,
    countInstallments
  } = useProductForm(connectorUid);

  useEffect(() => {
    loadPreferences();
    loadConnectorInfo();
    
    // Definir rota no breadcrumb
    consumer.defineRoute({ 
      name: "Novo Produto", 
      uri: `/management/store/connector/${connectorUid}/product/new` 
    });
  }, [connectorUid, connectorInfo]);

  async function loadPreferences() {
    try {
      const res = await fetch('./preferences.json');
      const prefs = await res.json();
      setPreferences(prefs);
    } catch (error) {
      console.error('Erro ao carregar preferências:', error);
    }
  }

  function loadConnectorInfo() {
    try {
      const connector = StoreProductsService.getConnectorInfo(connectorUid);
      setConnectorInfo(connector);
    } catch (error) {
      console.error('Erro ao carregar informações do connector:', error);
    }
  }

  function handleInputChange(field, value) {
    updateField(field, value);
  }

  function handleInstallmentChange(index, value) {
    updateInstallment(index, value);
  }

  async function handleSave() {
    setMessages({});

    const result = await saveProduct(false);
    
    if (result.success) {
      setMessages({ success: result.message });
      
      // Redirecionar para edição após 2 segundos
      setTimeout(() => {
        history.push(`/management/store/connector/${connectorUid}/product/${result.data.uid}/edit`);
      }, 2000);
    } else {
      setMessages({ error: result.message });
    }
  }

  function handleCancel() {
    history.push(`/management/store/connector/${connectorUid}/product/list`);
  }

  if (!preferences) {
    return <Loading msg="Carregando..." />;
  }

  return (
    <div className="rdp-admin-product-new">
      <Header consumer={consumer} />
      <Sidebar 
        consumer={consumer}
        defineGroup={consumer ? consumer.defineGroup : null}
        defineRoute={consumer ? consumer.defineRoute : null}
        groups={consumer ? consumer.groups : null}
      />
      <Breadcrumb currentRoute={consumer ? consumer.currentRoute : null} />
      
      <div className="rdp-admin-content">
        {messages.success && (
          <NotifyBox type="success" message={messages.success} />
        )}
        
        {messages.error && (
          <NotifyBox type="error" message={messages.error} />
        )}

        <div className="rdp-admin-product-new__form">
          <div className="form-section">
            <h3 className="form-section__title">Informações Básicas</h3>
            
            <div className="form-row">
              <div className={`form-field ${errors.name ? 'form-field--error' : ''}`}>
                <label className="form-field__label required">
                  {PRODUCT_FIELD_LABELS.name}
                </label>
                <input
                  type="text"
                  className="form-field__input"
                  value={product.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onBlur={generateUniqueName}
                  placeholder="Digite o nome do produto"
                />
                {errors.name && <span className="form-field__error">{errors.name}</span>}
              </div>

              <div className={`form-field ${errors.unique_name ? 'form-field--error' : ''}`}>
                <label className="form-field__label required">
                  {PRODUCT_FIELD_LABELS.unique_name}
                </label>
                <input
                  type="text"
                  className="form-field__input"
                  value={product.unique_name}
                  onChange={(e) => handleInputChange('unique_name', e.target.value)}
                  placeholder="Identificador único (ex: produto-123)"
                />
                {errors.unique_name && <span className="form-field__error">{errors.unique_name}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-field ${errors.currency ? 'form-field--error' : ''}`}>
                <label className="form-field__label required">
                  {PRODUCT_FIELD_LABELS.currency}
                </label>
                <select
                  className="form-field__select"
                  value={product.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                >
                  {CURRENCY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.currency && <span className="form-field__error">{errors.currency}</span>}
              </div>

              <div className={`form-field ${errors.amount ? 'form-field--error' : ''}`}>
                <label className="form-field__label required">
                  {PRODUCT_FIELD_LABELS.amount}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-field__input"
                  value={product.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="0.00"
                />
                {errors.amount && <span className="form-field__error">{errors.amount}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className={`form-field ${errors.installments_max ? 'form-field--error' : ''}`}>
                <label className="form-field__label">
                  {PRODUCT_FIELD_LABELS.installments_max}
                </label>
                <input
                  type="number"
                  min="0"
                  max="12"
                  className="form-field__input"
                  value={product.installments_max}
                  onChange={(e) => handleInputChange('installments_max', e.target.value)}
                  placeholder="0"
                />
                {errors.installments_max && <span className="form-field__error">{errors.installments_max}</span>}
              </div>
            </div>
          </div>

          {countInstallments > 0 && (
            <div className="form-section">
              <h3 className="form-section__title">Configuração de Parcelas</h3>
              
              <div className="installments-grid">
                {[...Array(countInstallments)].map((_, index) => {
                  const errorKey = `installment_${index}`;
                  return (
                    <div key={index} className={`form-field ${errors[errorKey] ? 'form-field--error' : ''}`}>
                      <label className="form-field__label">
                        Juros Parcela {index + 1} (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-field__input"
                        value={product.installments_tax?.[index] || ''}
                        onChange={(e) => handleInstallmentChange(index, e.target.value)}
                        placeholder="0.00"
                      />
                      {errors[errorKey] && <span className="form-field__error">{errors[errorKey]}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </button>
            
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSave}
              disabled={!isFormValid || saving}
            >
              {saving ? 'Salvando...' : 'Salvar Produto'}
            </button>
          </div>
        </div>
      </div>

      {saving && <Loading msg="Criando produto..." />}
    </div>
  );
}
